import { Typography } from "@outschool/backpack";
import {
  Activity,
  PaymentSchedule as PaymentScheduleGraphqlType,
  ScheduledPaymentLineItemType,
} from "@outschool/gql-backend-generated";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import * as Time from "@outschool/time";
import { DocumentNode } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  TrackedButton,
  TrackedButtonProps,
} from "@outschool/ui-components-shared";
import { useTopNoticeContext } from "@outschool/ui-components-website";
import { Modal } from "@outschool/ui-legacy-component-library";
import React, { useRef, useState } from "react";

import * as EnrollmentPaymentRefund from "../../../shared/EnrollmentPaymentRefund";
import * as Env from "../../../shared/Env";
import {
  AccountSectionNewQueryVariables,
  EnrolledClassDetailsQueryVariables,
} from "../../../shared/generated/graphql";
import * as PaymentSchedule from "../../../shared/PaymentSchedule";
import { useStopPaymentSchedule } from "../../hooks/PaymentSchedule";
import ErrorMessage from "../ErrorMessage";
import StopSubscriptionReasonForm from "./StopSubscriptionReasonForm";

type StopEnrollmentPaymentScheduleProps = TrackedButtonProps & {
  studentName: string;
  enrollment: {
    paymentSchedule?: Pick<PaymentScheduleGraphqlType, "uid"> | null;
  };
  activity: Pick<Activity, "title">;
  rootQuery?: {
    query: DocumentNode;
    variables:
      | AccountSectionNewQueryVariables
      | EnrolledClassDetailsQueryVariables;
  };
};

export default React.forwardRef(StopEnrollmentPaymentSchedule);

function StopEnrollmentPaymentSchedule(
  {
    studentName,
    enrollment,
    activity,
    rootQuery,
    onClick,
    ...buttonProps
  }: StopEnrollmentPaymentScheduleProps,
  ref: React.Ref<any>,
) {
  const [isOpen, setIsModalOpen] = useState(false);
  const { userTimeZone } = useSession();
  const { setTopNotice } = useTopNoticeContext();
  const stopPaymentSchedule = useStopPaymentSchedule({
    refetchQueries: rootQuery ? [rootQuery] : undefined,
    awaitRefetchQueries: true,
  });
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [mutationInProgress, setMutationInProgress] = useState(false);
  const [error, setError] = useState(null);
  const reasonInput = useRef<HTMLSelectElement>(null);
  const noteInput = useRef<HTMLTextAreaElement>(null);

  // Using this ref to prevent Warning: Can't perform a React state update on an unmounted component
  const mounted = useRef(false);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const onClose = () => {
    if (mounted.current) {
      setIsModalOpen(false);
    }
  };
  async function handleStopSubscriptionClick(
    event: React.FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();
    if (!reason) {
      // @ts-ignore TS(2531): Object is possibly 'null'.
      reasonInput && reasonInput.current.focus();
      // @ts-ignore TS(2345): Argument of type '"Please choose a reason"' is not... Remove this comment to see the full error message
      setError("Please choose a reason");
      return;
    }
    const trimmedNote = note.trim();
    if (!trimmedNote && reason === EnrollmentPaymentRefund.REASONS.OTHER) {
      noteInput?.current?.focus();
      // @ts-ignore TS(2345): Argument of type '"Please add a note with more det... Remove this comment to see the full error message
      setError("Please add a note with more details");
      return;
    }
    const stoppedReason = `Reason: ${reason}, Parent Note: ${trimmedNote}`;
    try {
      setMutationInProgress(true);
      await stopPaymentSchedule({
        // @ts-ignore TS(2532): Object is possibly 'undefined'.
        scheduleUid: enrollment.paymentSchedule.uid,
        scheduledPaymentLineItemType:
          ScheduledPaymentLineItemType.ClubSubscriptions,
        stoppedReason,
      });
      setTopNotice(
        `Stopped subscription for ${studentName} to ${activity.title}`,
      );
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      if (mounted.current) {
        setMutationInProgress(false);
      }
    }
  }
  const monthlyScheduleEndDate = PaymentSchedule.getPeriod(
    PaymentSchedule.RelativePeriodOptions.Current,
  ).MonthlyFirst[1];

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <Typography
          variant="h3"
          sx={{
            marginY: "0.5em",
          }}
        >
          Stop subscription for {studentName} to this group
        </Typography>
        This will stop future payments. {studentName} will no longer have access
        to this group after{" "}
        {/* @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message */}
        {Time.formatDate(monthlyScheduleEndDate, userTimeZone)}.
        <ErrorMessage
          value={error}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
        <StopSubscriptionReasonForm
          handleSubmit={handleStopSubscriptionClick}
          handleClose={onClose}
          onReasonDropdownChange={value => {
            setReason(value);
            setError(null);
          }}
          onReasonNoteChange={e => {
            setNote(e.target.value);
            setError(null);
          }}
          reason={reason}
          note={note}
          reasonInputRef={reasonInput}
          noteInputRef={noteInput}
          mutationInProgress={mutationInProgress}
        />
      </Modal>
      <TrackedButton
        ref={ref}
        variant="link"
        trackingName={"stop_enrollment_payment_schedule"}
        data-test-id={"stop-enrollment-payment-schedule"}
        onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          onClick?.(event);
          setIsModalOpen(true);
        }}
        {...buttonProps}
      >
        Stop Subscription
      </TrackedButton>
    </>
  );
}
